import type { ScheduleTRPCInput } from '@risksmart-app/trpc/src/types';
import { mapScheduleToTRPC } from '@risksmart-app/trpc/src/types';
import type {
  UpdateChildObligationInput,
  UpdateObligationMutation,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  namedOperations,
  UpdateObligationDocument,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

/**
 * The wrapper's public input. `OriginalTimestamp` is the optimistic-concurrency
 * token (COI-622) — the obligation row's last-seen `ModifiedAtTimestamp`. The
 * legacy GraphQL input has no such field, so it is stripped on the Apollo leg.
 */
type UpdateObligationInput = UpdateChildObligationInput & {
  OriginalTimestamp?: string | null;
};

type UpdateObligationTRPCInput = Omit<
  UpdateObligationInput,
  'schedule' | 'Adherence'
> &
  ScheduleTRPCInput & {
    Adherence: string;
  };

export const useUpdateObligation = createMutationHook({
  name: 'updateObligation',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.obligation.update.mutationOptions({ onSuccess }),
  mapInput: ({
    schedule,
    ...rest
  }: UpdateObligationInput): UpdateObligationTRPCInput => ({
    ...rest,
    ...mapScheduleToTRPC(schedule),
    Adherence: rest.Adherence ?? '',
  }),
  invalidate: (trpc) => [
    trpc.frontend.obligation.getById.queryKey(),
    trpc.frontend.obligation.register.queryKey(),
    trpc.frontend.obligation.obligationsByType.queryKey(),
    trpc.frontend.obligation.obligationList.queryKey(),
  ],
  // The tRPC update returns the full row; the legacy shape echoes the Id.
  mapResponse: (
    _result,
    input: UpdateObligationInput
  ): UpdateObligationMutation => ({
    updateChildObligation: {
      __typename: 'IdOutput',
      Id: input.Id,
    },
  }),
  graphqlDocument: UpdateObligationDocument,
  graphqlVariables: ({
    OriginalTimestamp: _token,
    ...input
  }: UpdateObligationInput) => ({ object: input }),
  evictFields: ['obligation'],
  refetchQueries: [namedOperations.Query.getObligationById],
});
